/**
 * ADR-0223 §3.5 — a session (`/app/collab/sessions/:id`) as minutes on
 * paper, and an occurrence (`/app/collab/series/:id/next`,
 * `…/occurrences/:date`, E12) as its agenda — Still open, New since then.
 *
 * One recording flow (the owner, 2026-11-04: "I'm lost in the process"):
 * *Record this session* creates the session at once and opens the minutes in
 * edit; in edit, every section carries its own add row and every change
 * saves as it is made. *Done* returns to the minutes. Every line is served.
 */

import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { apiFetch } from "../lib/api.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import { MemberPicker, type MemberCandidate } from "../components/MemberPicker.js";
import { CollabPage, errMessage, Faces, RaisedList, Thread, Tok, type OccurrencePage, type RaisedRow, type Session, type SessionAgenda } from "../components/collab.js";
import { loadRaiseCandidates, Picker } from "./CollabIdeaDetail.js";

type Mode = "session" | "next" | "occurrence";

type ItemPayload = Record<string, unknown>;

/** Pure, for the tests: the masthead and the four parts of the minutes. */
export function MinutesView(props: { session: Session }) {
  const s = props.session;
  return (
    <>
      <Masthead date_block={s.date_block} title={s.title}>
        {s.meta_line ? <span>{s.meta_line}</span> : null}
        {s.attendees.length ? (
          <>
            <Faces faces={s.attendees} />
            <span>{s.attended_line}</span>
          </>
        ) : null}
        {!s.recorded ? <Tok token="next" label="Scheduled" /> : null}
      </Masthead>
      {s.decisions.length ? (
        <section className="collabPart">
          <div className="collabPartHead">
            <h2 className="collabH2">Decided</h2>
            <span>{s.decisions.length}</span>
          </div>
          <div className="collabDecList">
            {s.decisions.map((d) => (
              <div className="collabDec" key={d.item_id}>
                <span className="collabCheck">✓</span>
                <div>
                  <div className="collabDecText">{d.text}</div>
                  <div className="collabDecWhere">
                    {d.attribution}
                    {d.initiative ? (
                      <>
                        {d.attribution ? " · " : ""}
                        On <Link to={d.initiative.href}>{d.initiative.ref} {d.initiative.name}</Link>
                      </>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}
      {s.raised.length ? (
        <section className="collabPart">
          <div className="collabPartHead">
            <h2 className="collabH2">Raised</h2>
            <span>{s.raised.length}, and where each one went</span>
          </div>
          <RaisedList rows={s.raised} />
        </section>
      ) : null}
      {s.learned.length ? (
        <section className="collabPart">
          <div className="collabPartHead">
            <h2 className="collabH2">Learned</h2>
            <span>{s.learned.length}</span>
          </div>
          <ul className="collabNoted">
            {s.learned.map((l) => (
              <li key={l.item_id}>
                <span>
                  {l.text}
                  <span className="collabWent">
                    <span className="collabArrow">→</span>
                    <span>{l.line}</span>
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {s.notes.length ? (
        <section className="collabPart">
          <div className="collabPartHead">
            <h2 className="collabH2">Noted</h2>
          </div>
          <ul className="collabNoted">
            {s.notes.map((n) => (
              <li key={n.item_id}>
                {n.text}
                {n.people.length ? <span className="collabPeople"> — {n.people.map((p) => p.name).join(", ")}</span> : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {s.recorded && s.decisions.length + s.raised.length + s.learned.length + s.notes.length === 0 ? <div className="collabEmpty">Nothing in the minutes yet.</div> : null}
      {s.carry ? (
        <Link className="collabCarry" to={s.carry.href}>
          <span>
            <b>{s.carry.count} still open</b> {s.carry.line.replace(/^\d+ still open /, "")}
          </span>
          <span className="go">See the agenda →</span>
        </Link>
      ) : null}
    </>
  );
}

export function Masthead(props: { date_block: { dow: string; day: string; month_year: string }; title: string; children?: React.ReactNode }) {
  return (
    <div className="collabMasthead">
      <div className="collabBigDate">
        <span className="dow">{props.date_block.dow}</span>
        <span className="d">{props.date_block.day}</span>
        <span className="my">{props.date_block.month_year}</span>
      </div>
      <div>
        <div className="collabMastTitle">{props.title}</div>
        <div className="collabMastMeta">{props.children}</div>
      </div>
    </div>
  );
}

/** Pure, for the tests: an upcoming occurrence's agenda. */
export function AgendaView(props: { agenda: SessionAgenda }) {
  const a = props.agenda;
  if (a.still_open.length === 0 && a.new_since.length === 0) {
    return (
      <section className="collabPart">
        <div className="collabPartHead">
          <h2 className="collabH2">Agenda</h2>
        </div>
        <div className="collabEmpty">{a.empty_line}</div>
      </section>
    );
  }
  return (
    <>
      {a.still_open.length ? (
        <section className="collabPart">
          <div className="collabPartHead">
            <h2 className="collabH2">Still open</h2>
            <span>{a.from_label}</span>
          </div>
          <RaisedList rows={a.still_open} />
        </section>
      ) : null}
      {a.new_since.length ? (
        <section className="collabPart">
          <div className="collabPartHead">
            <h2 className="collabH2">New since then</h2>
            <span>{a.new_since.length}</span>
          </div>
          <RaisedList rows={a.new_since} />
        </section>
      ) : null}
    </>
  );
}

type Vocab = { knowledge_types: string[]; guidance_categories: string[] };
export type AddKind = "decision" | "note" | "idea" | "issue" | "link" | "task" | "learning";

const ADD_LABEL: Record<AddKind, string> = {
  decision: "+ Add a decision",
  note: "+ Add a note",
  idea: "+ Raise a new idea",
  issue: "+ Raise an issue",
  link: "+ Raise something already known",
  task: "+ Add a task",
  learning: "+ Add a learning",
};

/** One add row (§3.5): what it produces is the hub's item payload, saved as soon as *Add* is pressed. */
export function AddItemRow(props: { kind: AddKind; users: MemberCandidate[]; attendeeIds: string[]; vocab: Vocab; initiatives: Array<{ id: string; label: string }>; onAdd: (payload: ItemPayload, summary: string) => Promise<void> | void; onCancel: () => void }) {
  const [text, setText] = useState("");
  const [people, setPeople] = useState<string[]>([]);
  const [who, setWho] = useState("");
  const [due, setDue] = useState("");
  const [initiative, setInitiative] = useState("");
  const [tree, setTree] = useState<"knowledge" | "guidance">("knowledge");
  const [typeOrCategory, setTypeOrCategory] = useState("");
  const [linkItems, setLinkItems] = useState<Array<{ id: string; label: string; sub?: string }> | null>(null);
  const [busy, setBusy] = useState(false);
  const attendees = props.users.filter((u) => props.attendeeIds.includes(u.id));
  const whoOptions = attendees.length ? attendees : props.users;

  useEffect(() => {
    if (props.kind === "link" && linkItems === null) void loadRaiseCandidates().then(setLinkItems);
  }, [props.kind, linkItems]);

  async function add(payload: ItemPayload, summary: string) {
    if (busy) return;
    setBusy(true);
    try {
      await props.onAdd(payload, summary);
      setText("");
      setPeople([]);
      setDue("");
      setInitiative("");
      setTypeOrCategory("");
    } finally {
      setBusy(false);
    }
  }

  if (props.kind === "link") {
    return (
      <div className="collabAddRow">
        <Picker
          items={linkItems ?? []}
          loading={linkItems === null}
          placeholder="An idea, issue or action item that came up"
          onPick={(id) => {
            const [k, ref] = id.split(":") as ["idea" | "issue" | "action_item", string];
            const label = linkItems?.find((i) => i.id === id)?.label ?? ref;
            void add(k === "idea" ? { kind: "raised", idea_id: ref } : k === "issue" ? { kind: "raised", issue_id: ref } : { kind: "raised", action_item_id: ref }, label);
          }}
          onCancel={props.onCancel}
        />
      </div>
    );
  }

  const needsWho = props.kind === "idea" || props.kind === "issue" || props.kind === "task";
  const canAdd = text.trim().length > 0 && (!needsWho || props.kind === "issue" || who) && (props.kind !== "learning" || typeOrCategory);
  const labels: Record<AddKind, string> = { decision: "The decision", note: "A point of fact", idea: "The idea, in one line", issue: "What is in the way", link: "", task: "The task", learning: "What was learned" };

  return (
    <div className="collabAddRow">
      <textarea className="collabInput" rows={2} value={text} maxLength={props.kind === "idea" || props.kind === "issue" || props.kind === "task" ? 200 : 2000} placeholder={labels[props.kind]} aria-label={labels[props.kind]} onChange={(e) => setText(e.target.value)} />
      <div className="collabRowInputs">
        {props.kind === "decision" || props.kind === "note" || props.kind === "learning" ? (
          <div>
            <MemberPicker mode="multi" allowedKinds={["user"]} candidates={whoOptions} selected={people} onChange={setPeople} placeholder="Who said it" />
          </div>
        ) : null}
        {props.kind === "decision" ? (
          <select className="collabInput" value={initiative} onChange={(e) => setInitiative(e.target.value)} aria-label="On an initiative">
            <option value="">Not on an initiative</option>
            {props.initiatives.map((i) => (
              <option key={i.id} value={i.id}>
                {i.label}
              </option>
            ))}
          </select>
        ) : null}
        {needsWho ? (
          <select className="collabInput" value={who} onChange={(e) => setWho(e.target.value)} aria-label={props.kind === "idea" ? "Raised by" : "Assigned to"}>
            <option value="">{props.kind === "idea" ? "Raised by…" : props.kind === "issue" ? "Unassigned" : "Assigned to…"}</option>
            {whoOptions.map((u) => (
              <option key={u.id} value={u.id}>
                {u.display_name}
              </option>
            ))}
          </select>
        ) : null}
        {props.kind === "task" ? <input className="collabInput" type="date" value={due} onChange={(e) => setDue(e.target.value)} aria-label="Due" /> : null}
        {props.kind === "learning" ? (
          <>
            <select
              className="collabInput"
              value={tree}
              onChange={(e) => {
                setTree(e.target.value as "knowledge" | "guidance");
                setTypeOrCategory("");
              }}
              aria-label="Knowledge or guidance"
            >
              <option value="knowledge">Knowledge — a fact about the business</option>
              <option value="guidance">Guidance — how to work</option>
            </select>
            <select className="collabInput" value={typeOrCategory} onChange={(e) => setTypeOrCategory(e.target.value)} aria-label={tree === "knowledge" ? "Type" : "Category"}>
              <option value="">{tree === "knowledge" ? "Type…" : "Category…"}</option>
              {(tree === "knowledge" ? props.vocab.knowledge_types : props.vocab.guidance_categories).map((v) => (
                <option key={v} value={v}>
                  {v.replace(/_/g, " ")}
                </option>
              ))}
            </select>
          </>
        ) : null}
      </div>
      <div className="collabPost" style={{ justifyContent: "flex-start", marginTop: 0 }}>
        <button
          type="button"
          className="collabBtn collabBtnAmber"
          disabled={busy || !canAdd}
          onClick={() => {
            const t = text.trim();
            if (props.kind === "decision") void add({ kind: "decision", text: t, people_account_ids: people, ...(initiative ? { initiative } : {}) }, t);
            else if (props.kind === "note") void add({ kind: "note", text: t, people_account_ids: people }, t);
            else if (props.kind === "idea") void add({ kind: "raised", new_idea: { title: t, raised_by_account_id: who } }, t);
            else if (props.kind === "issue") void add({ kind: "raised", new_issue: { title: t, ...(who ? { assignee_account_id: who } : {}) } }, t);
            else if (props.kind === "task") void add({ kind: "raised", new_task: { title: t, assignee_account_id: who, ...(due ? { due } : {}) } }, t);
            else void add({ kind: "learning", text: t, tree, ...(tree === "knowledge" ? { type: typeOrCategory } : { category: typeOrCategory }), people_account_ids: people }, t);
          }}
        >
          Add
        </button>
        <button type="button" className="collabBtn" onClick={props.onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/** A section of the minutes in edit: its rows, then its own add row or the buttons that open one. */
function EditSection(props: { title: string; count: number; hint?: string; kinds: AddKind[]; adding: AddKind | null; setAdding: (k: AddKind | null) => void; builder: boolean; addRow: (kind: AddKind) => React.ReactNode; children: React.ReactNode }) {
  const open = props.adding && props.kinds.includes(props.adding) ? props.adding : null;
  return (
    <section className="collabPart">
      <div className="collabPartHead">
        <h2 className="collabH2">{props.title}</h2>
        <span>{props.hint ?? props.count}</span>
      </div>
      {props.children}
      {open ? (
        props.addRow(open)
      ) : (
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
          {props.kinds
            .filter((k) => k !== "issue" || props.builder)
            .map((k) => (
              <button type="button" className="collabLinkBtn" key={k} onClick={() => props.setAdding(k)}>
                {ADD_LABEL[k]}
              </button>
            ))}
        </div>
      )}
    </section>
  );
}

export function CollabSession(props: { mode: Mode; entitlement?: string }) {
  const { id, date } = useParams<{ id: string; date?: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const builder = props.entitlement === "builder" || props.entitlement === "admin";
  const [session, setSession] = useState<Session | null>(null);
  const [occurrence, setOccurrence] = useState<OccurrencePage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(params.get("edit") === "1");
  const [adding, setAdding] = useState<AddKind | null>(null);
  const [users, setUsers] = useState<MemberCandidate[]>([]);
  const [initiatives, setInitiatives] = useState<Array<{ id: string; label: string }>>([]);
  const [header, setHeader] = useState({ attendees: [] as string[], duration: "", location: "", starts_at: "", held_on: "", title: "" });
  const [busy, setBusy] = useState(false);

  async function load() {
    if (!id) return;
    try {
      if (props.mode === "session") {
        const r = await apiFetch<{ session: Session }>(`/api/tenant/collab/sessions/${encodeURIComponent(id)}`);
        setSession(r.session);
        setHeader({ attendees: r.session.attendee_account_ids, duration: r.session.duration_minutes ? String(r.session.duration_minutes) : "", location: r.session.location ?? "", starts_at: r.session.starts_at ?? "", held_on: r.session.held_on, title: r.session.kind === "one_off" ? r.session.title : "" });
      } else {
        const path = props.mode === "next" ? `/api/tenant/collab/series/${encodeURIComponent(id)}/next` : `/api/tenant/collab/series/${encodeURIComponent(id)}/occurrences/${encodeURIComponent(date ?? "")}`;
        const r = await apiFetch<OccurrencePage | { session_id: string }>(path);
        if ("session_id" in r) {
          navigate(`/app/collab/sessions/${r.session_id}`, { replace: true });
          return;
        }
        setOccurrence(r);
      }
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    setLoading(true);
    setError(null);
    setSession(null);
    setOccurrence(null);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.mode, id, date]);

  useEffect(() => {
    if (!editing) return;
    void apiFetch<{ candidates: MemberCandidate[] }>("/api/tenant/rasci-candidates")
      .then((r) => setUsers(r.candidates.filter((c) => c.kind === "user")))
      .catch(() => setUsers([]));
    void apiFetch<{ initiatives: Array<{ id: string; ref?: string; name: string; lifecycle: string }> }>("/api/tenant/initiatives")
      .then((r) => setInitiatives(r.initiatives.filter((i) => i.lifecycle === "active").map((i) => ({ id: i.id, label: `${i.ref ? `${i.ref} ` : ""}${i.name}` }))))
      .catch(() => setInitiatives([]));
  }, [editing]);

  // ── every change saves as it is made ──
  async function patchSession(body: Record<string, unknown>) {
    if (!session) return;
    try {
      const r = await apiFetch<{ session: Session }>(`/api/tenant/collab/sessions/${encodeURIComponent(session.id)}`, { method: "PATCH", body });
      setSession(r.session);
    } catch (e) {
      toast.push({ kind: "error", message: errMessage(e) });
    }
  }
  async function addItem(payload: ItemPayload) {
    if (!session) return;
    try {
      const r = await apiFetch<{ session: Session }>(`/api/tenant/collab/sessions/${encodeURIComponent(session.id)}/items`, { method: "POST", body: payload });
      setSession(r.session);
      setAdding(null);
    } catch (e) {
      toast.push({ kind: "error", message: errMessage(e) });
      throw e;
    }
  }
  async function patchItem(itemId: string, body: Record<string, unknown>) {
    if (!session) return;
    try {
      const r = await apiFetch<{ session: Session }>(`/api/tenant/collab/sessions/${encodeURIComponent(session.id)}/items/${encodeURIComponent(itemId)}`, { method: "PATCH", body });
      setSession(r.session);
    } catch (e) {
      toast.push({ kind: "error", message: errMessage(e) });
    }
  }
  async function removeItem(itemId: string, what: string) {
    if (!session) return;
    if (!(await confirm.confirm({ title: "Remove from the minutes?", detail: what, confirmLabel: "Remove", destructive: true }))) return;
    try {
      const r = await apiFetch<{ session: Session }>(`/api/tenant/collab/sessions/${encodeURIComponent(session.id)}/items/${encodeURIComponent(itemId)}`, { method: "DELETE" });
      setSession(r.session);
    } catch (e) {
      toast.push({ kind: "error", message: errMessage(e) });
    }
  }

  /** The occurrence's *Record this session*: the session is created at once, with the members as attendees, and opens in edit. */
  async function recordNow() {
    if (!occurrence || busy) return;
    setBusy(true);
    try {
      const r = await apiFetch<{ session: { id: string } }>("/api/tenant/collab/sessions", {
        method: "POST",
        body: { series_id: occurrence.series.id, held_on: occurrence.occurrence.date, attendee_account_ids: occurrence.member_account_ids },
      });
      navigate(`/app/collab/sessions/${r.session.id}?edit=1`, { replace: true });
    } catch (e) {
      toast.push({ kind: "error", message: errMessage(e) });
    } finally {
      setBusy(false);
    }
  }

  async function postChatter(body: string) {
    if (!session) return;
    try {
      await apiFetch(`/api/tenant/collab/sessions/${encodeURIComponent(session.id)}/chatter`, { method: "POST", body: { body } });
      await load();
    } catch (e) {
      toast.push({ kind: "error", message: errMessage(e) });
    }
  }
  async function editChatter(messageId: string, body: string) {
    try {
      await apiFetch(`/api/tenant/collab/chatter/${encodeURIComponent(messageId)}`, { method: "PATCH", body: { body } });
      await load();
    } catch (e) {
      toast.push({ kind: "error", message: errMessage(e) });
    }
  }
  async function redactChatter(messageId: string) {
    const reason = await confirm.confirmWithReason({ title: "Redact this message", confirmLabel: "Redact", destructive: true, reasonField: { label: "Reason", placeholder: "Why it is removed", maxLength: 2000 } });
    if (!reason) return;
    try {
      await apiFetch(`/api/tenant/admin/collab/chatter/${encodeURIComponent(messageId)}/redact`, { method: "POST", body: { reason } });
      await load();
    } catch (e) {
      toast.push({ kind: "error", message: errMessage(e) });
    }
  }

  const seriesSummary = session?.series ?? occurrence?.series ?? null;
  const crumb = (
    <span className="collabCrumb">
      <Link to="/app/collab/meetings">Meetings</Link> / {seriesSummary ? <Link to={`/app/collab/series/${seriesSummary.id}`}>{seriesSummary.name}</Link> : session ? <Link to="/app/collab/meetings/one-off">One-off</Link> : "…"}
    </span>
  );
  const vocab: Vocab = session?.learning_vocab ?? occurrence?.learning_vocab ?? { knowledge_types: [], guidance_categories: [] };
  const addRow = (kind: AddKind) => <AddItemRow kind={kind} users={users} attendeeIds={header.attendees} vocab={vocab} initiatives={initiatives} onAdd={(p) => addItem(p)} onCancel={() => setAdding(null)} />;

  return (
    <CollabPage title={seriesSummary?.name ?? session?.title ?? "Session"} crumb={crumb}>
      {error ? <ErrorState detail={error} /> : null}
      {!error && loading ? <LoadingState /> : null}
      {!error && !loading && session ? (
        <article className="collabSheet">
          <div className="collabSheetMain">
            {editing ? (
              <>
                <Masthead date_block={session.date_block} title={session.title}>
                  <button type="button" className="collabBtn collabBtnAmber" onClick={() => { setEditing(false); setAdding(null); }}>
                    Done
                  </button>
                </Masthead>
                <div className="collabAddRow" style={{ background: "transparent", padding: 0 }}>
                  <div className="collabLabel">
                    Attended
                    <MemberPicker
                      mode="multi"
                      allowedKinds={["user"]}
                      candidates={users}
                      selected={header.attendees}
                      onChange={(next) => {
                        setHeader((h) => ({ ...h, attendees: next }));
                        void patchSession({ attendee_account_ids: next });
                      }}
                      placeholder="Who was there"
                    />
                  </div>
                  <div className="collabHeaderInputs">
                    {session.kind === "one_off" ? (
                      <>
                        <label className="collabLabel" style={{ gridColumn: "1 / -1" }}>
                          Subject
                          <input className="collabInput" value={header.title} maxLength={200} onChange={(e) => setHeader((h) => ({ ...h, title: e.target.value }))} onBlur={() => { if (header.title.trim() && header.title.trim() !== session.title) void patchSession({ title: header.title.trim() }); }} />
                        </label>
                        <label className="collabLabel">
                          Date
                          <input className="collabInput" type="date" value={header.held_on} onChange={(e) => setHeader((h) => ({ ...h, held_on: e.target.value }))} onBlur={() => { if (header.held_on && header.held_on !== session.held_on) void patchSession({ held_on: header.held_on }); }} />
                        </label>
                      </>
                    ) : null}
                    <label className="collabLabel">
                      Starts
                      <input className="collabInput" type="time" value={header.starts_at} onChange={(e) => setHeader((h) => ({ ...h, starts_at: e.target.value }))} onBlur={() => void patchSession({ starts_at: header.starts_at || null })} />
                    </label>
                    <label className="collabLabel">
                      Minutes
                      <input className="collabInput" inputMode="numeric" value={header.duration} onChange={(e) => setHeader((h) => ({ ...h, duration: e.target.value }))} onBlur={() => void patchSession({ duration_minutes: header.duration ? Number(header.duration) : null })} />
                    </label>
                    <label className="collabLabel">
                      Where
                      <input className="collabInput" value={header.location} maxLength={100} placeholder="Teams" onChange={(e) => setHeader((h) => ({ ...h, location: e.target.value }))} onBlur={() => void patchSession({ location: header.location.trim() || null })} />
                    </label>
                  </div>
                </div>
                <EditSection title="Decided" count={session.decisions.length} kinds={["decision"]} adding={adding} setAdding={setAdding} builder={builder} addRow={addRow}>
                  {session.decisions.map((d) => (
                    <div className="collabEditRow" key={d.item_id}>
                      <div>
                        <textarea
                          className="collabInput"
                          rows={2}
                          defaultValue={d.text}
                          maxLength={2000}
                          aria-label="Decision"
                          onBlur={(e) => {
                            if (e.target.value.trim() && e.target.value.trim() !== d.text) void patchItem(d.item_id, { text: e.target.value.trim() });
                          }}
                        />
                        <span className="collabPeople">
                          {d.attribution}
                          {d.initiative ? ` · On ${d.initiative.ref} ${d.initiative.name}` : ""}
                        </span>
                      </div>
                      <button type="button" className="collabLinkBtn isDanger" onClick={() => void removeItem(d.item_id, d.text)}>
                        Remove
                      </button>
                    </div>
                  ))}
                </EditSection>
                <EditSection title="Raised" count={session.raised.length} hint={`${session.raised.length}, and where each one went`} kinds={["idea", "issue", "link", "task"]} adding={adding} setAdding={setAdding} builder={builder} addRow={addRow}>
                  <RaisedList rows={session.raised} onRemove={(r: RaisedRow) => r.item_id && void removeItem(r.item_id, r.title)} />
                </EditSection>
                <EditSection title="Learned" count={session.learned.length} kinds={["learning"]} adding={adding} setAdding={setAdding} builder={builder} addRow={addRow}>
                  {session.learned.map((l) => (
                    <div className="collabEditRow" key={l.item_id}>
                      <div>
                        <div>{l.text}</div>
                        <span className="collabPeople">{l.line}</span>
                      </div>
                      <button type="button" className="collabLinkBtn isDanger" onClick={() => void removeItem(l.item_id, l.text)}>
                        Remove
                      </button>
                    </div>
                  ))}
                </EditSection>
                <EditSection title="Noted" count={session.notes.length} kinds={["note"]} adding={adding} setAdding={setAdding} builder={builder} addRow={addRow}>
                  {session.notes.map((n) => (
                    <div className="collabEditRow" key={n.item_id}>
                      <textarea
                        className="collabInput"
                        rows={2}
                        defaultValue={n.text}
                        maxLength={2000}
                        aria-label="Note"
                        onBlur={(e) => {
                          if (e.target.value.trim() && e.target.value.trim() !== n.text) void patchItem(n.item_id, { text: e.target.value.trim() });
                        }}
                      />
                      <button type="button" className="collabLinkBtn isDanger" onClick={() => void removeItem(n.item_id, n.text)}>
                        Remove
                      </button>
                    </div>
                  ))}
                </EditSection>
                <div>
                  <button type="button" className="collabBtn collabBtnAmber" onClick={() => { setEditing(false); setAdding(null); }}>
                    Done
                  </button>
                </div>
              </>
            ) : (
              <>
                <MinutesView session={session} />
                {session.can.edit ? (
                  <div>
                    <button type="button" className="collabBtn" onClick={() => setEditing(true)}>
                      {session.recorded ? "Edit the minutes" : "Record this session"}
                    </button>
                  </div>
                ) : null}
              </>
            )}
          </div>
          <aside className="collabSheetSide">
            <div>
              <div className="collabSideHead">{session.series ? "About this meeting" : "One-off"}</div>
              <div className="collabSideText">{session.about_line}</div>
            </div>
            <div>
              <div className="collabSideHead">Chatter</div>
              <Thread messages={session.chatter} prompt="Add to the chatter…" onPost={postChatter} onEdit={editChatter} onRedact={props.entitlement === "admin" ? redactChatter : undefined} />
            </div>
          </aside>
        </article>
      ) : null}
      {!error && !loading && occurrence ? (
        <article className="collabSheet">
          <div className="collabSheetMain">
            <Masthead date_block={occurrence.date_block} title={occurrence.series.name}>
              {occurrence.occurrence.time ? <span>{occurrence.occurrence.time}</span> : null}
              <Tok token={occurrence.occurrence.state} label={occurrence.occurrence.state === "no_record" ? "No record" : "Upcoming"} />
            </Masthead>
            <AgendaView agenda={occurrence.agenda} />
            {occurrence.can.record ? (
              <div>
                <button type="button" className="collabBtn collabBtnAmber" disabled={busy} onClick={() => void recordNow()}>
                  Record this session
                </button>
                <span className="collabHelp" style={{ marginLeft: 12 }}>Opens the minutes to fill in; the agenda stays here until it is raised.</span>
              </div>
            ) : null}
          </div>
          <aside className="collabSheetSide">
            <div>
              <div className="collabSideHead">About this meeting</div>
              <div className="collabSideText">
                {occurrence.series.purpose ? (
                  <>
                    {occurrence.series.purpose}
                    <br />
                    <br />
                  </>
                ) : null}
                {occurrence.series.meets_line}
              </div>
            </div>
            <div>
              <div className="collabSideHead">Chatter</div>
              <div className="collabEmpty">Chatter opens once the session is recorded.</div>
            </div>
          </aside>
        </article>
      ) : null}
    </CollabPage>
  );
}
